/**
 * Notification Stack Component
 *
 * Renders stacked snackbar notifications from the notification context
 * Listens to globalNotification events and adds them to the queue
 */

import { useEffect } from "react";
import { useNotification } from "context/NotificationContext";
import SoftSnackbar from "components/SoftSnackbar";

// Icon mapping for notification types
const getIconForType = (type) => {
  switch (type) {
    case "success":
      return "check";
    case "error":
      return "warning";
    case "warning":
      return "star";
    case "info":
    default:
      return "notifications";
  }
};

// Color mapping for notification types
const getColorForType = (type) => {
  switch (type) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
    default:
      return "info";
  }
};

// Format date time for display
const formatDateTime = (date) => {
  if (!date) {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }
  if (typeof date === "string") {
    return date;
  }
  if (date instanceof Date) {
    return `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }
  return "now";
};

function NotificationStack() {
  const { notifications, addNotification, removeNotification } =
    useNotification();

  // Listen to globalNotification events
  useEffect(() => {
    const handleGlobalNotification = (event) => {
      const { detail } = event;
      const notificationType = detail.type || "info";
      const notification = {
        type: notificationType,
        title: detail.title || "Notification",
        message:
          detail.message || detail.Message || "New notification received",
        dateTime: detail.dateTime || formatDateTime(new Date()),
        icon: detail.icon || getIconForType(notificationType),
        color: detail.color || getColorForType(notificationType),
        bgWhite:
          detail.bgWhite !== undefined
            ? detail.bgWhite
            : notificationType === "success" ||
              notificationType === "warning" ||
              notificationType === "error",
        // Store flags from detail
        removeOnSuccess: detail.removeOnSuccess || false,
      };

      addNotification(notification);

      // If this is a success notification, remove any processing notifications
      if (notificationType === "success") {
        // Use setTimeout to ensure the notification was added first, then check and remove processing ones
        setTimeout(() => {
          // Access current notifications through the state
          // We'll need to check notifications in a separate effect
        }, 0);
      }
    };

    window.addEventListener("globalNotification", handleGlobalNotification);

    return () => {
      window.removeEventListener(
        "globalNotification",
        handleGlobalNotification
      );
    };
  }, [addNotification]);

  // Remove processing notifications when success arrives
  useEffect(() => {
    // Check if the last notification is a success notification
    const lastNotification = notifications[notifications.length - 1];
    if (lastNotification && lastNotification.type === "success") {
      // Find and remove any processing notifications that should be removed on success
      const processingNotifications = notifications.filter(
        (n) => n.removeOnSuccess === true && n.id !== lastNotification.id
      );
      processingNotifications.forEach((n) => {
        removeNotification(n.id);
      });
    }
  }, [notifications, removeNotification]);

  // Calculate vertical offset for stacking (each snackbar is approximately 110px tall)
  const getOffset = (index) => {
    const baseOffset = 24; // Base offset from bottom (matches MUI default)
    const snackbarHeight = 110; // Approximate height of each snackbar
    const spacing = 16; // Spacing between snackbars
    // Reverse index so newest notifications appear at the bottom
    return baseOffset + index * (snackbarHeight + spacing);
  };

  return (
    <>
      {notifications.map((notification, index) => {
        // Calculate offset - newest notifications (last in array) get lower offset
        const reversedIndex = notifications.length - index - 1;
        const bottomOffset = getOffset(reversedIndex);

        // Create a unique close handler for each notification
        const handleClose = (event) => {
          if (event) {
            event.stopPropagation();
          }
          removeNotification(notification.id);
        };

        const handleSnackbarClose = (event, reason) => {
          // Only close if user explicitly clicks close button, not on clickaway
          if (reason === "clickaway") {
            return;
          }
          if (event) {
            event.stopPropagation();
          }
          removeNotification(notification.id);
        };

        return (
          <SoftSnackbar
            key={notification.id}
            color={notification.color}
            icon={notification.icon}
            title={notification.title}
            dateTime={notification.dateTime}
            content={notification.message}
            open={true}
            onClose={handleSnackbarClose}
            close={handleClose}
            bgWhite={notification.bgWhite}
            autoHideDuration={null}
            sx={{
              bottom: `${bottomOffset}px !important`,
              zIndex: 1400 + index,
            }}
          />
        );
      })}
    </>
  );
}

export default NotificationStack;
